/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { ArrowLeft, Package, Hash, Box } from "lucide-react";
import { motion } from "motion/react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { ViewTransition } from "react";
import ImagePlaceholder from "@/components/custom/image-placeholder";
import Section from "@/components/custom/section";
import { Button } from "@/components/ui/button";
import type { Product } from "@/lib/data";
import { cn } from "@/lib/utils";

type ProductDetailProps = {
	readonly product: Product;
	readonly category: string;
	readonly purchaseDescriptionFromItems?: string;
	readonly nameFromItems?: string;
};

export default function ProductDetail({
	product,
	category,
	purchaseDescriptionFromItems,
	nameFromItems,
}: ProductDetailProps) {
	const router = useRouter();

	// Animation variants
	const slideInVariants: any = {
		hidden: { opacity: 0, x: -30 },
		visible: {
			opacity: 1,
			x: 0,
			transition: {
				duration: 0.3,
				ease: [0.22, 1, 0.36, 1],
			},
		},
	};

	const itemVariants: any = {
		hidden: { opacity: 0, y: 20 },
		visible: {
			opacity: 1,
			y: 0,
			transition: {
				duration: 0.25,
				ease: [0.22, 1, 0.36, 1],
				delay: 0,
			},
		},
	};

	const itemVariantsDelay: any = {
		hidden: { opacity: 0, y: 20 },
		visible: {
			opacity: 1,
			y: 0,
			transition: {
				duration: 0.25,
				ease: [0.22, 1, 0.36, 1],
				delay: 0.2,
			},
		},
	};

	const detailsVariants: any = {
		hidden: { opacity: 0, y: 20 },
		visible: {
			opacity: 1,
			y: 0,
			transition: {
				duration: 0.25,
				ease: [0.22, 1, 0.36, 1],
				delay: 0.3,
				staggerChildren: 0.05,
				delayChildren: 0.1,
			},
		},
	};

	const detailRowVariants: any = {
		hidden: { opacity: 0, y: 20 },
		visible: {
			opacity: 1,
			y: 0,
			transition: {
				duration: 0.2,
				ease: [0.22, 1, 0.36, 1],
			},
		},
	};

	const lineVariants: any = {
		hidden: { scaleX: 0 },
		visible: {
			scaleX: 1,
			transition: {
				duration: 0.6,
				delay: 0.3,
				ease: [0.22, 1, 0.36, 1],
			},
		},
	};

	const isSalF4 = product?.sku === "SAL-F4";

	return (
		<Section>
			{isSalF4 && (
				<style
					dangerouslySetInnerHTML={{
						__html: `
@media (max-width: 1023px) {
  .product-description-sal-f4 { width: 100%; overflow-x: hidden; }
  .product-description-sal-f4 > div { max-width: 100% !important; min-width: 0 !important; }
  .product-description-sal-f4 > div > div:nth-of-type(1) { grid-template-columns: 1fr !important; min-width: 0 !important; }
  .product-description-sal-f4 > div > div:nth-of-type(1) > div,
  .product-description-sal-f4 > div > div:nth-of-type(1) > a > div { flex-wrap: wrap !important; min-width: 0 !important; }
  .product-description-sal-f4 > div > div:nth-of-type(1) img { max-width: 100% !important; width: auto !important; min-width: 80px !important; }
  .product-description-sal-f4 table { width: 100% !important; min-width: 0 !important; table-layout: fixed !important; display: table !important; }
  .product-description-sal-f4 table td { word-wrap: break-word; overflow-wrap: break-word; word-break: break-word; }
}
						`.trim(),
					}}
				/>
			)}
			<div className="min-h-screen pt-12 md:pt-4 overflow-x-clip">
				<div className="max-w-6xl mx-auto pt-6">
					{/* Back button */}
					<motion.div
						className="mb-8"
						variants={slideInVariants}
						initial="hidden"
						animate="visible"
					>
						<Button
							variant="ghost"
							size="sm"
							onClick={() => router.back()}
							className="group/back hover:bg-primary/10 transition-all duration-300"
						>
							<ArrowLeft className="size-4 transition-transform duration-300 group-hover/back:-translate-x-1" />
							Torna indietro
						</Button>
					</motion.div>

					{/* Product Badge */}
					<motion.div
						initial={{ opacity: 0, y: -10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5 }}
						className="mb-6"
					>
						<span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium tracking-wide uppercase">
							<span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
							Product
						</span>
					</motion.div>

					<div className="flex flex-col lg:flex-row gap-10 mb-10">
						{/* Image Container with decorative frame */}
						<div className="flex justify-center lg:justify-start shrink-0">
							<ViewTransition
								name={`product-image-${product?.sku ?? product?.item_id ?? ""}`}
							>
								<motion.div
									className="relative"
									initial={{ opacity: 0, scale: 0.95 }}
									animate={{ opacity: 1, scale: 1 }}
									transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
								>

									{product?.sku ? (
										<div className="relative w-[280px] h-[280px] md:w-[320px] md:h-[320px] p-6 rounded-2xl bg-white border border-border/50 shadow-lg shadow-primary/5 overflow-hidden">
											<Image
												unoptimized
												src={`/images/from_catalog/${product.sku}.png`}
												alt="Product image"
												fill
												style={{ objectFit: "contain" }}
												className="p-4"
											/>
										</div>
									) : (
										<div className="relative w-[280px] h-[280px] md:w-[320px] md:h-[320px] p-6 rounded-2xl bg-card border border-border/50 shadow-lg flex items-center justify-center">
											<ImagePlaceholder />
										</div>
									)}
								</motion.div>
							</ViewTransition>
						</div>

						{/* Product Info */}
						<motion.div
							className="space-y-6 flex-1"
							variants={itemVariants}
							initial="hidden"
							animate="visible"
						>
							<div className="space-y-4">
								<ViewTransition
									name={`product-name-${product?.sku ?? product?.item_id ?? ""}`}
								>
									<h1 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight">
										{nameFromItems ?? product?.name}
									</h1>
								</ViewTransition>

								{/* Decorative line under title */}
								<motion.div
									variants={lineVariants}
									initial="hidden"
									animate="visible"
									className="h-1 w-20 bg-linear-to-r from-primary to-secondary rounded-full origin-left"
								/>

								<motion.div
									variants={itemVariantsDelay}
									initial="hidden"
									animate="visible"
									className="flex flex-wrap items-center gap-3 pt-2"
								>
									{/* Category badge */}
									<Link
										href={`/categories/${encodeURIComponent(category)}`}
										className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-primary bg-primary/10 rounded-full hover:bg-primary/20 transition-all duration-300 cursor-pointer"
									>
										<span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
										{category.toUpperCase()}
									</Link>

									{/* SKU badge */}
									{product?.sku && (
										<span className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-muted-foreground bg-muted rounded-full">
											<Hash className="w-3.5 h-3.5" />
											{product.sku}
										</span>
									)}
								</motion.div>
							</div>
						</motion.div>
					</div>

					{/* Product Details Section */}
					<motion.div
						className="bg-transparent"
						variants={detailsVariants}
						initial="hidden"
						animate="visible"
					>
						{/* Section title with decorative line */}
						<motion.div
							className="flex items-center gap-4 mb-6"
							variants={detailsVariants}
							initial="hidden"
							animate="visible"
						>
							<Package className="w-5 h-5 text-primary" />
							<h2 className="text-2xl font-bold text-foreground">
								Product details
							</h2>
							<div className="flex-1 h-px bg-linear-to-r from-border to-transparent" />
						</motion.div>

						{(purchaseDescriptionFromItems || product?.description) && (
							<motion.div
								className="mb-8 p-6 rounded-2xl bg-card border border-border/50"
								variants={detailRowVariants}
							>
								<div
									className={cn(
										"text-muted-foreground leading-relaxed prose prose-sm max-w-none",
										"[&_table]:w-full [&_table]:max-w-full [&_table]:my-4 [&_table]:border-collapse [&_table]:border-2 [&_table]:border-solid [&_table]:border-border [&_table]:rounded-[8px] [&_table]:overflow-hidden [&_table]:bg-gray-50/30",
										"[&_tr]:border-b [&_tr]:border-solid [&_tr]:border-border [&_tr:last-child]:border-b-0",
										"[&_td]:px-4 [&_td]:py-3 [&_td:first-child]:font-semibold [&_td:first-child]:text-foreground [&_td:last-child]:text-muted-foreground",
										"[&_h4]:text-lg [&_h4]:font-semibold [&_h4]:text-foreground [&_h4]:mt-6 [&_h4]:mb-3",
										"[&_ul]:list-disc [&_ul]:ml-6 [&_ul]:space-y-1 [&_ul]:my-4 [&_li]:text-muted-foreground",
										"[&_p]:my-2",
										isSalF4 && "product-description-sal-f4",
									)}
									dangerouslySetInnerHTML={{
										__html:
											purchaseDescriptionFromItems ?? product.description ?? "",
									}}
								/>
							</motion.div>
						)}

						{/* Footer with SKU/Unit */}
						<motion.div
							className="flex flex-wrap gap-6 text-sm text-muted-foreground pt-4 border-t border-border/50 pb-8"
							variants={detailRowVariants}
						>
							{product?.sku && (
								<span className="inline-flex items-center gap-2">
									<Hash className="w-4 h-4 text-primary/60" />
									<span className="font-medium text-foreground">SKU:</span>
									{product.sku}
								</span>
							)}
							{product?.unit && (
								<span className="inline-flex items-center gap-2">
									<Box className="w-4 h-4 text-primary/60" />
									<span className="font-medium text-foreground">Unit:</span>
									{product.unit}
								</span>
							)}
						</motion.div>
					</motion.div>
				</div>
			</div>
		</Section>
	);
}
